import * as React from "react";
import { AccountHolder } from "../../Account";
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLanguage } from "../../Language";
import {
  SavedSearchTypeKey,
  SavedSearchTypeToggle,
} from "../../SavedSearchTypeToggle";
import { LoaderFunctionArgs, useLoaderData } from "react-router-dom";
import axios from "axios";
import { LoaderData } from "./MyAccount";
import {
  DynamiResourceListView,
  SavedSearchesService,
} from "./searches/SavedSearches";
import {
  DeleteListReturn,
  ListType,
  ResourceListSortType,
} from "./ListService";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { TLCEventManager } from "../../../events/EventManager";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import * as dayjs from "dayjs";
import { DeleteListDialog } from "./lists/DeleteListDialog";
import { RenameListDialog } from "./lists/RenameListDialog";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";

interface ListCountReturn {
  count: number;
}

export async function savedSearchesLoader({ params }: LoaderFunctionArgs) {
  const defaultPageSize: number = 10;
  const defaultPage: number = 0;
  const defaultSortType: ResourceListSortType =
    ResourceListSortType.CreationDate;
  const personalCount: number = (
    await axios.get<ListCountReturn>(`list/count/dynamic`)
  ).data.count;
  await AccountHolder.fetchAccountData(); // necessary for AccountHolder.isConfigAdmin
  const configCount: number = AccountHolder.isConfigAdmin
    ? (await axios.get<ListCountReturn>(`list/count/dynamic/config`)).data.count
    : null;
  const defaultTab: SavedSearchTypeKey = AccountHolder.isConfigAdmin
    ? "configListType"
    : "personalListType";
  const searches = await SavedSearchesService.getSearches(
    AccountHolder.isConfigAdmin,
    defaultPage,
    defaultPageSize,
    defaultSortType
  );

  return {
    searches,
    defaultPageSize,
    defaultPage,
    defaultSortType,
    defaultTab,
    personalCount,
    configCount,
  };
}

export const SavedSearchesPage: React.FC = () => {
  const {
    configCount: initialConfigCount,
    personalCount: initialPersonalCount,
    searches,
    defaultPage,
    defaultPageSize,
    defaultSortType,
    defaultTab,
  } = useLoaderData() as LoaderData<typeof savedSearchesLoader>;
  const { translate } = useLanguage();
  const [personalCount, setPersonalCount] =
    React.useState<number>(initialPersonalCount);
  const [configCount, setConfigCount] =
    React.useState<number>(initialConfigCount);

  const [listType, setListType] =
    React.useState<SavedSearchTypeKey>(defaultTab);

  const [displayedSearches, setDisplayedSearches] =
    React.useState<DynamiResourceListView[]>(searches);

  const [batchDeleteError, setBatchDeleteError] =
    React.useState<boolean>(false);

  const isConfig = React.useMemo<boolean>(
    () => listType === "configListType",
    [listType]
  );
  const total = React.useMemo<number>(
    () => (isConfig ? configCount : personalCount),
    [isConfig, configCount, personalCount]
  );

  const [sortType, setSortType] =
    React.useState<ResourceListSortType>(defaultSortType);
  const [page, setPage] = React.useState<number>(defaultPage);
  const [pageSize, setPageSize] = React.useState<number>(defaultPageSize);

  const [listToDelete, setListToDelete] =
    React.useState<DynamiResourceListView>(null);

  const [listToRename, setListToRename] =
    React.useState<DynamiResourceListView>(null);
  const [selected, setSelected] = React.useState<DynamiResourceListView[]>([]);
  const selectedRows = React.useMemo<DynamiResourceListView["id"][]>(
    () => selected.map((row) => row.id),
    [selected]
  );

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const allRows = await await SavedSearchesService.getSearches(
        isConfig,
        0,
        total,
        sortType
      );
      setSelected(allRows);
      return;
    }
    setSelected([]);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    row: DynamiResourceListView
  ) => {
    const { id } = row;
    const selectedIndex = selectedRows.indexOf(id);

    if (selectedIndex === -1) {
      setSelected([...selected, row]);
    } else {
      setSelected(selected.filter((row) => row.id !== id));
    }
  };

  const changeHandler = async (
    event: React.MouseEvent<HTMLElement>,
    newValue: SavedSearchTypeKey
  ) => {
    setListType(newValue);
    setPage(0);
    const searches = await SavedSearchesService.getSearches(
      newValue === "configListType", // isConfig won't have settled at this point
      0,
      pageSize,
      sortType
    );
    setDisplayedSearches(searches);
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
    const searches = await SavedSearchesService.getSearches(
      isConfig,
      newPage,
      pageSize,
      sortType
    );
    setDisplayedSearches(searches);
  };
  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(size);
    setPage(0);
    const searches = await SavedSearchesService.getSearches(
      isConfig,
      0,
      size,
      sortType
    );
    setDisplayedSearches(searches);
  };

  const handleSortChange = async function (event: SelectChangeEvent) {
    const newSort = event.target.value as ResourceListSortType;
    setSortType(newSort);
    setPage(0);
    const searches = await SavedSearchesService.getSearches(
      isConfig,
      0,
      pageSize,
      newSort
    );
    setDisplayedSearches(searches);
  };

  const onClickDelete = async (event: React.MouseEvent) => {
    try {
      const { data } = await axios.post<DeleteListReturn[]>(`lists/delete`, {
        listIds: selectedRows,
      });

      AccountHolder.fetchAccountSummaryAndPatron(true); // update account menu list count
      const deleted = data
        .map(({ success }, index) => (success ? selectedRows[index] : null))
        .filter((row) => row);
      const successCount = deleted.length;

      if (successCount > 0) {
        SuccessMessageControl.show(
          `${successCount} ${translate("removedFromSavedSearches")}`
        );

        if (listType === "configListType") {
          setConfigCount(configCount - successCount);
        } else {
          setPersonalCount(personalCount - successCount);
        }

        // setSelected(selected.filter((row) => deleted.indexOf(row.id) === -1));
        setSelected([]);
        const newCount = total - successCount;
        const pageStart = page * pageSize;
        const newPage =
          newCount <= 0
            ? 0
            : newCount <= pageStart
            ? Math.ceil(newCount / pageSize) - 1
            : page;
        handleChangePage(newPage);
      } else {
        const errorCount = data.filter((value) => !value.success).length;
        if (errorCount > 0) {
          setBatchDeleteError(true);
        }
      }
    } catch (e) {
      setBatchDeleteError(true);
    }
  };

  return (
    <Box>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          {selected.length > 0 ? (
            <Toolbar
              sx={{
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity
                  ),
              }}
            >
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selected.length} {translate("selected")}
              </Typography>
              <Tooltip title={translate("removeSelected")}>
                <IconButton onClick={onClickDelete}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          ) : (
            <Toolbar>
              <Stack
                direction={"row"}
                gap={1}
                sx={{ flex: "1 1", alignItems: "center" }}
              >
                {AccountHolder.isConfigAdmin && (
                  <SavedSearchTypeToggle
                    value={listType}
                    personalCount={personalCount}
                    configCount={configCount}
                    onChange={changeHandler}
                  ></SavedSearchTypeToggle>
                )}
                <Typography variant="h6">
                  {!AccountHolder.isConfigAdmin && personalCount + " "}
                  {translate("savedSearches")}
                </Typography>
              </Stack>

              <FormControl>
                <InputLabel id="list-sort-label">
                  {translate("sortBy")}
                </InputLabel>
                <Select
                  size="small"
                  labelId="list-sort-label"
                  disabled={displayedSearches.length === 0}
                  label={translate("sortBy")}
                  value={sortType}
                  onChange={handleSortChange}
                >
                  <MenuItem value={ResourceListSortType.Name}>
                    {translate("nameSort")}
                  </MenuItem>
                  <MenuItem value={ResourceListSortType.CreationDate}>
                    {translate("creationDateSort")}
                  </MenuItem>
                  <MenuItem value={ResourceListSortType.LastUpdateDate}>
                    {translate("lastUpdateDateSort")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Toolbar>
          )}
          {displayedSearches.length > 0 && (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={
                          selected.length > 0 && selected.length < total
                        }
                        checked={total > 0 && selected.length === total}
                        onChange={handleSelectAllClick}
                        inputProps={{
                          "aria-label": translate("selectAll"),
                        }}
                      />
                    </TableCell>

                    <TableCell>{translate("searchName")}</TableCell>
                    <TableCell></TableCell>
                    <TableCell>{translate("searchCriteria")}</TableCell>
                    <TableCell>{translate("sortedBy")}</TableCell>
                    <TableCell>{translate("date")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedSearches.map((search, index) => {
                    const isItemSelected =
                      selectedRows.indexOf(search.id) !== -1;
                    const labelId = `table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, search)}
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                        role="checkbox"
                        tabIndex={-1}
                        key={search.id}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              "aria-labelledby": labelId,
                            }}
                          />
                        </TableCell>

                        <TableCell id={labelId} scope="row">
                          <Button
                            onClick={() => {
                              TLCEventManager.fireEvent(
                                LS2PAC_CONSTANTS.Events.onSearch,
                                {
                                  searchString: search.expression,
                                  sortKey: search.sortType,
                                  branchFilters: search.branchFilters,
                                  facetFilters: search.facetFilters,
                                  readingLevelFilters:
                                    search.readingLevelFilters,
                                }
                              );
                            }}
                          >
                            {search.name}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            aria-label={translate("rename")}
                            onClick={(event) => {
                              event.stopPropagation(); // prevent row click
                              setListToRename(search);
                            }}
                          >
                            <DriveFileRenameOutlineIcon />
                          </IconButton>
                          <IconButton
                            component={"a"}
                            target="_blank"
                            href={`/list/dynamic/${search.id}/rss`}
                            aria-label="Go to {resourceUtil.getSavedSearchExpression} rss feed"
                          >
                            <RssFeedIcon />
                          </IconButton>
                          <IconButton
                            aria-label={translate("remove")}
                            onClick={(event) => {
                              event.stopPropagation(); // prevent row click
                              setListToDelete(search);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {translate("searchQuery")}
                          {": "}
                          {search.advanced &&
                            "(" + translate("advancedSearch") + ")"}
                          {!search.advanced && search.expression}
                          {(search.facetFilters || []).map((filter, index) => (
                            <>
                              {filter.facetName}
                              {": "}
                              {filter.facetValue}
                              {index < search.facetFilters.length - 1 && ", "}
                            </>
                          ))}
                        </TableCell>
                        <TableCell>{translate(search.sortType)}</TableCell>
                        <TableCell>
                          {dayjs(search.creationDate).format(
                            "YYYY-MM-DD HH:mm:ss"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={total}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(event, newPage) => handleChangePage(newPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
          {displayedSearches.length === 0 && (
            <Alert severity="info">{translate("noSavedSearchesMessage")}</Alert>
          )}
        </TableContainer>
      </Paper>
      <DeleteListDialog
        open={!!listToDelete}
        onClose={(deleted) => {
          if (deleted) {
            const listId = listToDelete.id;
            const updated = displayedSearches.filter(
              (search) => search.id !== listId
            );
            if (listType === "configListType") {
              setConfigCount(configCount - 1);
            } else {
              setPersonalCount(personalCount - 1);
            }
            setDisplayedSearches(updated);
            setSelected(selected.filter((row) => row.id !== listId));
          }
          setListToDelete(null);
        }}
        listId={listToDelete?.id}
        listType={isConfig ? ListType.Config : ListType.Personal}
        successMessage={translate("removedFromSavedSearches")}
        errorMessage={translate("removedFromSavedSearchesError")}
      ></DeleteListDialog>
      <RenameListDialog
        name={listToRename?.name}
        id={listToRename?.id}
        type={isConfig ? ListType.Config : ListType.Personal}
        title={translate("renameSavedSearchDialogTitle")}
        successMessage={translate("renamedSavedSearchSuccess")}
        errorMessage={translate("renamedSavedSearchError")}
        open={!!listToRename}
        onClose={() => setListToRename(null)}
        onSave={(newListName) => {
          const updated = displayedSearches.map((search) =>
            search.id === listToRename.id
              ? {
                  ...search,
                  name: newListName,
                }
              : search
          );
          setDisplayedSearches(updated);
          setListToRename(null);
        }}
      ></RenameListDialog>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={batchDeleteError}
        onClose={() => setBatchDeleteError(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
          {translate("removedFromSavedSearchesError")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// TODO row selection is going to be a repeated feature, make it more DRY
